import { Paper, Table, Text } from '@mantine/core'
import type { OrpcClient } from '@/orpc/client'
import { formatCents } from '../utils'

export type ReportGroup = Awaited<ReturnType<OrpcClient['billingV2Report']>>['groups'][number]

export function SummaryTable({
  title,
  groupByLabel,
  columns,
  groups,
  totalCents,
  note,
  onDrillDown,
}: {
  title: string
  groupByLabel: string
  columns: string[]
  groups: ReportGroup[]
  totalCents: number[]
  note: string
  onDrillDown: (group: ReportGroup) => void
}) {
  return (
    <Paper withBorder radius="md" className="overflow-hidden p-4 shadow-sm">
      <Text component="h3" fw={600} mb="sm">
        {title}
      </Text>

      <Table.ScrollContainer minWidth={600}>
        <Table horizontalSpacing="sm" verticalSpacing="xs">
          <Table.Thead className="bg-[#F8FBFF]">
            <Table.Tr>
              <Table.Th>{groupByLabel}</Table.Th>
              {columns.map((column) => (
                <Table.Th key={column} ta="right">
                  {column}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {groups.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={columns.length + 1}>
                  <Text size="sm" c="dimmed" ta="center">
                    No records match these filters.
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              groups.map((group) => (
                <Table.Tr key={group.key}>
                  <Table.Td>
                    <button
                      type="button"
                      className="cursor-pointer text-left text-blue-600 hover:underline"
                      onClick={() => onDrillDown(group)}
                    >
                      {`${group.label} →`}
                    </button>
                  </Table.Td>
                  {group.valuesCents.map((cents, index) => (
                    <Table.Td key={columns[index] ?? index} ta="right">
                      {formatCents(cents)}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))
            )}
          </Table.Tbody>

          <Table.Tfoot>
            <Table.Tr className="font-semibold">
              <Table.Td>Consolidated total</Table.Td>
              {totalCents.map((cents, index) => (
                <Table.Td key={columns[index] ?? index} ta="right">
                  {formatCents(cents)}
                </Table.Td>
              ))}
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>

      <Text size="xs" c="dimmed" mt="xs" aria-live="polite">
        {note}
      </Text>
    </Paper>
  )
}
